import {Component, EventEmitter, inject, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray} from '@angular/cdk/drag-drop';
import {forkJoin} from 'rxjs';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faBuilding, faGripDotsVertical, faMagnifyingGlass, faUsers} from '@fortawesome/pro-solid-svg-icons';
import {
  BicButtonComponent,
  BicInputComponent,
  BicPopupComponent,
  BicToastService,
} from '@buildinginformationcloud/bic-ui-components';

import {CheckDefinition, MyChecksService} from '../../my-checks.service';
import {CheckPinService, PinScope} from '../../../../services/check-pin.service';
import {UserPreferenceService} from '../../../../services/user-preference.service';
import {StateConfigService} from '../../../../services/state/state-config.service';

/**
 * Verwaltung der Seitenleisten-Favoriten: welche Prüfläufe als Shortcut erscheinen und in welcher
 * Reihenfolge. Aus beiden Tabs der myCheck-Ansicht erreichbar; lädt seine Daten selbst, damit die
 * Aufrufer nichts davon kennen müssen.
 */
@Component({
  selector: 'app-favorites-prompt',
  standalone: true,
  imports: [
    FormsModule,
    FaIconComponent,
    BicPopupComponent,
    BicButtonComponent,
    BicInputComponent,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './favorites-prompt.component.html',
})
export class FavoritesPromptComponent implements OnInit {
  @Output() closed = new EventEmitter<void>();

  protected loading = true;
  protected saving = false;
  protected search = '';
  protected order: CheckDefinition[] = [];

  protected readonly faMagnifyingGlass = faMagnifyingGlass;
  protected readonly faGripDotsVertical = faGripDotsVertical;
  protected readonly faBuilding = faBuilding;
  protected readonly faUsers = faUsers;

  private pending = new Map<string, boolean>();
  private pinnedIds = new Set<string>();
  private scopes = new Map<string, PinScope>();

  private myChecksService = inject(MyChecksService);
  private pinService = inject(CheckPinService);
  private prefService = inject(UserPreferenceService);
  private stateConfigService = inject(StateConfigService);
  private toastService = inject(BicToastService);

  ngOnInit(): void {
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId) {
      this.loading = false;
      return;
    }

    forkJoin([
      this.myChecksService.getAvailableChecks(projectId),
      this.pinService.listPins(projectId),
      this.prefService.get(projectId),
    ]).subscribe({
      next: ([available, pins, rawOrder]) => {
        const visiblePins = pins.filter(pin => pin.scope === 'ORGANISATION' || pin.projectId === projectId);
        this.pinnedIds = new Set(visiblePins.map(pin => pin.definitionId));
        this.scopes = new Map(visiblePins.map(pin => [pin.definitionId, pin.scope]));

        const savedOrder = this.parseOrder(rawOrder);
        const checks = available.custom;
        this.order = savedOrder.length
          ? [
            ...savedOrder.map(id => checks.find(c => c.id === id)).filter((c): c is CheckDefinition => !!c),
            ...checks.filter(c => !savedOrder.includes(c.id)),
          ]
          : [...checks];
        this.pending = new Map(this.order.map(c => [c.id, this.pinnedIds.has(c.id)]));
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastService.error('Favoriten konnten nicht geladen werden');
      },
    });
  }

  protected get total(): number {
    return this.order.length;
  }

  protected get pinnedCount(): number {
    let count = 0;
    for (const pinned of this.pending.values()) {
      if (pinned) count++;
    }
    return count;
  }

  protected get filtered(): CheckDefinition[] {
    const query = this.search.trim().toLowerCase();
    if (!query) return this.order;
    return this.order.filter(c => c.name.toLowerCase().includes(query));
  }

  protected isPending(checkId: string): boolean {
    return this.pending.get(checkId) ?? false;
  }

  protected getScope(checkId: string): PinScope | null {
    return this.scopes.get(checkId) ?? null;
  }

  protected toggle(checkId: string): void {
    this.pending.set(checkId, !this.isPending(checkId));
  }

  protected onDrop(event: CdkDragDrop<CheckDefinition[]>): void {
    moveItemInArray(this.order, event.previousIndex, event.currentIndex);
  }

  protected reset(): void {
    this.pending = new Map(this.order.map(c => [c.id, this.pinnedIds.has(c.id)]));
  }

  protected onClose(): void {
    if (this.saving) return;
    this.closed.emit();
  }

  protected onSave(): void {
    const projectId = this.stateConfigService.selectedProject?.id;
    if (!projectId || this.saving) return;

    this.saving = true;
    const orderJson = JSON.stringify(this.order.map(c => c.id));
    // Nur geänderte Pins anfassen: ein erneutes setPin würde den Scope eines projektweit
    // geteilten Shortcuts auf "nur für mich" zurückdrehen.
    const pinOps = this.order
      .filter(c => this.isPending(c.id) !== this.pinnedIds.has(c.id))
      .map(c => this.isPending(c.id)
        ? this.pinService.setPin(c.id, {projectScoped: false, organisationScoped: false}, projectId)
        : this.pinService.unpin(c.id, projectId));

    forkJoin([this.prefService.set(projectId, orderJson), ...pinOps]).subscribe({
      next: () => {
        this.pinService.notifyPinsChanged();
        this.saving = false;
        this.toastService.success('Favoriten aktualisiert');
        this.closed.emit();
      },
      error: () => {
        this.saving = false;
        this.toastService.error('Favoriten konnten nicht gespeichert werden');
      },
    });
  }

  private parseOrder(raw: string | null): string[] {
    if (!raw) return [];
    try {
      return JSON.parse(raw) as string[];
    } catch {
      return [];
    }
  }
}
